import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePWA, InstallBanner, InstallGuide, subscribeToPush, disablePushNotifications } from './public/hooks/usePWA';
import api from './api/api';
import { CartProvider } from './context/CartContext';
import { OnboardingProvider } from './onboarding/OnboardingContext';
import useAnalytics from './public/hooks/useAnalytics';
import { pageToPath, pathToPage, pathToNavParams } from './utils/urlSync';
import { SUPPORTED_LANGUAGES, changeLanguage as changeAppLanguage } from './utils/supportedLanguages';
import { destinationForIntent, consumeIntent } from './utils/campaignIntent';
import { useRoleContext } from './context/RoleContext';
import ContextEpochBoundary from './context/ContextEpochBoundary';
import { contextTransitionState } from './context/contextTransition';
import { getAccessToken } from './api/tokenStore';
import useNavigationShell from './navigation/NavigationShell';
import { homeForRole } from './navigation/navigationRegistry';
import { activeBusinessNameFor } from './context/businessGrouping';
import { evaluateDestination } from './navigation/navigationPolicy';
import HomeFeed           from './public/pages/HomeFeed';
import Welcome            from './public/pages/Welcome';
import MyProfile          from './public/pages/MyProfile';
import RoleActivation     from './public/pages/RoleActivation';
import Onboarding          from './public/pages/Onboarding';
import AddProfilePhoto     from './public/pages/AddProfilePhoto';
import FlashSales          from './public/pages/FlashSales';
import PickupPoints        from './public/pages/PickupPoints';
import RouteCoverageMap   from './public/pages/RouteCoverageMap';
import Activity       from './public/pages/Activity';
import BottomNav      from './public/components/BottomNav';
import ProfileSwitcherSheet from './public/components/ProfileSwitcherSheet';
import PostModal      from './public/components/PostModal';
import CreateMomentModal from './public/components/CreateMomentModal';
import BecomeSellerInfo from './public/pages/BecomeSellerInfo';
import ConfirmDelivery    from './public/pages/ConfirmDelivery';
import SellerCheckout     from './public/pages/SellerCheckout';
import SellerCustomers   from './public/pages/SellerCustomers';
import AgentEarnings     from './public/pages/AgentEarnings';
import SellerPayouts     from './public/pages/SellerPayouts';
import SellerWallet      from './public/pages/SellerWallet';
import TransportProviderSettings from './public/pages/TransportProviderSettings';
import SellerAnalytics            from './public/pages/SellerAnalytics'; // eslint-disable-line
import BecomeTransportProvider    from './public/pages/BecomeTransportProvider'; // eslint-disable-line
import TransportAdmin             from './admin/pages/TransportAdmin'; // eslint-disable-line
import VanOperations              from './admin/pages/VanOperations'; // eslint-disable-line
import HubAdmin                   from './admin/pages/HubAdmin'; // eslint-disable-line
import SellerTeam                 from './public/pages/SellerTeam'; // eslint-disable-line
import AgentScorecard             from './public/pages/AgentScoreCard'; // eslint-disable-line
import Wishlist                   from './public/pages/Wishlist'; // eslint-disable-line
import CommerceProfile            from './public/pages/CommerceProfile'; // eslint-disable-line
import Services                   from './public/pages/Services'; // eslint-disable-line
import ServiceDetail               from './public/pages/ServiceDetail'; // eslint-disable-line
import PostService                 from './public/pages/PostService'; // eslint-disable-line
import MyServices                  from './public/pages/MyServices'; // eslint-disable-line
import TransportProviderDashboard from './public/pages/TransportProviderDashboard'; // eslint-disable-line
import SellerInbox       from './public/pages/SellerInbox';
import Stores from './public/pages/Stores';
import SellerStore from './public/pages/SellerStore';
import ClassifiedsPublic from './public/pages/ClassifiedsPublic';
import PublicLogin from './public/pages/PublicLogin';
import Register from './public/pages/Register';
import ProductDetail from './public/pages/ProductDetail';
import ClassifiedDetail from './public/pages/ClassifiedDetail';
import OrderTracking       from './public/pages/OrderTracking';
import MyOrders from './public/pages/MyOrders';
import CustomerProfile from './public/pages/CustomerProfile';
import EditPublicProfile from './public/pages/EditPublicProfile';
import PayoutSettings from './public/pages/PayoutSettings';
import BecomeSeller from './public/pages/BecomeSeller';
import SellerAccessGate from './public/components/SellerAccessGate';
import SellerDashboard from './public/pages/SellerDashboard';
import BusinessDashboard from './public/pages/BusinessDashboard';
import MyBusinesses from './public/pages/MyBusinesses';
import BusinessHome from './public/pages/BusinessHome';
import BecomeBusinessServiceProvider from './public/pages/BecomeBusinessServiceProvider';
import BecomeBusinessCapability from './public/pages/BecomeBusinessCapability';
import ConnectSelling from './public/pages/ConnectSelling';
import { momentActorProfileId } from './public/utils/publicActor';
import BecomeBusiness from './public/pages/BecomeBusiness';
import BecomeAgent from './public/pages/BecomeAgent';
import AgentDashboard from './public/pages/AgentDashboard';
import Cart from './public/pages/Cart';
import Checkout from './public/pages/Checkout';
import Search from './public/pages/Search';
import VerifyReceipt from './public/pages/VerifyReceipt';
import SellerProducts from './public/pages/SellerProducts';
import MyBrands from './public/pages/MyBrands';
import BrandDashboard from './public/pages/BrandDashboard';
import BrandCatalog from './public/pages/BrandCatalog';
import VerifyProduct from './public/pages/VerifyProduct';
import MyWarranties from './public/pages/MyWarranties';
import SellerWarrantyClaims from './public/pages/SellerWarrantyClaims';
import POS from './public/pages/POS';
import SellerClassifieds from './public/pages/SellerClassifieds';
import SellerOrders from './public/pages/SellerOrders';
import SellerInvoices from './public/pages/SellerInvoices';
import SellerShipping from './public/pages/SellerShipping';
import PayInvoice from './public/pages/PayInvoice';
import Dashboard from './admin/pages/Dashboard';
import Products from './admin/pages/Products';
import Classifieds from './admin/pages/Classifieds';
import Profile from './admin/pages/Profile';
import Users from './admin/pages/Users';
import SuperAgents from './admin/pages/SuperAgents';
import Disputes from './admin/pages/Disputes';
import Payouts from './admin/pages/Payouts';
import Invoices from './admin/pages/Invoices';
import Reports from './admin/pages/Reports';
import Orders from './admin/pages/Orders';
import AdminShipments from './admin/pages/AdminShipments';
import AdminDispatchReminders from './admin/pages/AdminDispatchReminders';
import ContactMessages from './admin/pages/ContactMessages';
import Announcements from './admin/pages/Announcements';
import Analytics from './admin/pages/Analytics';
import Payments from './admin/pages/Payments';
import Sellers from './admin/pages/Sellers';
import Businesses from './admin/pages/Businesses';
import AdminServices from './admin/pages/Services';
import IdentityVerifications from './admin/pages/IdentityVerifications';
import AdminBrands from './admin/pages/AdminBrands';
import AdminBrandAuthorizations from './admin/pages/AdminBrandAuthorizations';
import AdminWarrantyClaims from './admin/pages/AdminWarrantyClaims';
import OfficialProducts from './admin/pages/OfficialProducts';
import Agents from './admin/pages/Agents';
import SuperAgentDashboard from './public/pages/SuperAgentDashboard';
import TrackParcel from './public/pages/TrackParcel';
import DispatcherManifest from './public/pages/DispatcherManifest';
import HubReceive from './public/pages/HubReceive';
import ZoneManagement from './admin/pages/ZoneManagement';
import BodaRates from './admin/pages/BodaRates';
import RouteManagement from './admin/pages/RouteManagement';
import AgentPerformance from './admin/pages/AgentPerformance';
import FinancialDashboard from './admin/pages/FinancialDashboard';
import CollectionFees from './admin/pages/CollectionFees';
import BatchHandoff from './public/pages/BatchHandoff';
import SuperAgentParcel from './public/pages/SuperAgentParcel';
import SuperAgentSettings from './public/pages/SuperAgentSettings';
import SellerShipment from './public/pages/SellerShipment';
import SendShipment from './public/pages/SendShipment';
import MyShipments from './public/pages/MyShipments';
import BuyerParcelAction from './public/pages/BuyerParcelAction';
import StoreSettings from './seller/pages/StoreSettings';
import BecomeSuperAgentInfo from './public/pages/BecomeSuperAgentInfo';
import CategoryPage from './public/pages/CategoryPage';
import ListingsPage from './public/pages/ListingsPage';
import AboutUs from './public/pages/AboutUs';
import ContactUs from './public/pages/ContactUs';
import HowItWorks from './public/pages/HowItWorks';
import TermsAndConditions from './public/pages/TermsAndConditions';
import PrivacyPolicy from './public/pages/PrivacyPolicy';
import UpdateNotice from './public/components/UpdateNotice';
import TrainingSmsNotice from './public/components/TrainingSmsNotice';
import { identityState, afterIdentitySent } from './utils/identityState';
import useIdentityRefresh from './utils/useIdentityRefresh';
import useAgentPresence from './utils/useAgentPresence';

// Register service worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .catch(() => {}); // Silent fail - SW is progressive enhancement
  });
}

function App() {
  const { t, i18n } = useTranslation();
  const roleContext = useRoleContext();
  const {
    status: authStatus,
    isAuthenticated: isLoggedIn,
    activeRoleType: userRole,
    activeContext,
    activeProfile,
    roleOptions,
    contextEpoch,
    switching: roleSwitching,
    acceptAuthResponse,
    switchRole,
    refreshContext,
    logout: roleLogout,
  } = roleContext;
  const { showInstallPrompt, showGuide, handleInstall, handleDismiss, closeGuide,
    canInstall, environment } = usePWA();
  const [showPostModal, setShowPostModal] = useState(false);
  const [showMomentModal, setShowMomentModal] = useState(false);
  const [momentModalMode, setMomentModalMode] = useState('selling');
  const [momentRefreshKey, setMomentRefreshKey] = useState(0);
  // Single source of truth for every unread-inbox badge (bottom nav,
  // anywhere else that wants it) — GET /business/inbox/unread-count
  // combines seller-side + buyer-side unread conversations, deliberately
  // NOT the generic /notifications/unread-count some pages read today
  // (a different, never-reconciled number — see backend's
  // ConversationService.getUnreadConversationCount comment).
  const [inboxUnread, setInboxUnread] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [page, setPage]             = useState(() => {
    // Bootstrap from a real URL — lets external links (WhatsApp, SMS) deep-link
    // straight into tracking instead of always landing on Home.
    // e.g. https://kentexa.com/?track=KTX-ORD-47  →  TrackParcel-KTX-ORD-47
    try {
      const params = new URLSearchParams(window.location.search);
      const track   = params.get('track');
      const receive = params.get('receive');
      const confirm = params.get('confirm');
      const token   = params.get('token');
      if (params.get('notification') === '1' || /^\d+$/.test(params.get('notificationId') || '')) return 'Activity';
      if (confirm && token) return `ConfirmDelivery-${token}`;
      // Seller's private new-order link (SMS / WhatsApp): signed token; the page asks for login first.
      const sellerCheckout = params.get('sellerCheckout');
      if (sellerCheckout && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(sellerCheckout)) return `SellerCheckout-${sellerCheckout}`;
      if (receive && /^KTX-[A-Z0-9-]+$/i.test(receive)) return `BuyerParcelAction-${receive}`;
      if (track) return `TrackParcel-${track}`;
      // Real shareable paths (/product/42, /store/7, ...) — resolves
      // synchronously from the URL alone, no network round trip needed.
      const fromPath = pathToPage(window.location.pathname);
      if (fromPath) return fromPath;
    } catch { /* SSR or no window — fall through */ }
    return 'Home';
  });
  const [navParams, setNavParams]   = useState(() => {
    // Companion to the /store/:id?cp= boot-time deep-link above — carries
    // WHICH of that account's profiles (personal/business/hub/transport)
    // to show, since CommerceProfile.js can't tell from the bare id alone.
    try {
      return pathToNavParams(window.location.pathname, window.location.search);
    } catch { return null; }
  });
  const [navHistory, setNavHistory] = useState([]);
  // Show language picker only on first visit
  const [showLangPicker, setShowLangPicker] = useState(() => !localStorage.getItem('kentexa_lang'));

  // Landing Localization L1 (§11-12): ?intent= capture itself lives in
  // utils/campaignIntent.js, executed synchronously at module load — same
  // timing as i18n.js's ?lang= resolution, and for the same reason: Welcome.js
  // reads getIntent() synchronously on its first render, and sessionStorage
  // isn't reactive, so a React effect here would run too late (after first
  // paint) for that render to ever see it. Found via local browser
  // acceptance testing.

  // Commerce profiles enrich labels and business-scope parameters only.
  // Active authority is the server-issued AccountRole in RoleContextProvider.
  const myProfiles = roleOptions;
  const [showProfileSwitcher, setShowProfileSwitcher] = useState(false);
  const [roleSwitchError, setRoleSwitchError] = useState('');

  const fetchInboxUnread = () => {
    if (!isLoggedIn) { setInboxUnread(0); return; }
    api.get('/business/inbox/unread-count')
      .then(res => setInboxUnread(res.data?.unread || 0))
      .catch(() => {});
  };

  useEffect(() => { fetchInboxUnread(); }, [isLoggedIn, contextEpoch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Identity-verification architecture audit (2026-08-28): fetched once at
  // app level so requireVerifiedSeller() below can gate EVERY seller-
  // back-office entry point (Quick Actions tile, direct deep link, bottom
  // nav) from one place, instead of each page independently discovering
  // it's ungated. null = not loaded yet (never treated as "verified" —
  // renderPage() shows nothing/blocks rather than flashing a real page
  // then yanking it away).
  const [identityStatus, setIdentityStatus] = useState(null);
  useIdentityRefresh(setIdentityStatus, isLoggedIn);
  // D1: an agent's "online" stays on while any KenteXa screen is open.
  useAgentPresence(isLoggedIn, roleContext.availableRoles);
  const fetchIdentityStatus = () => {
    if (!isLoggedIn) { setIdentityStatus({ status: 'not_submitted', level: 0 }); return; }
    api.get('/identity/me')
      .then(res => setIdentityStatus(res.data))
      .catch(() => setIdentityStatus({ status: 'not_submitted', level: 0 }));
  };
  useEffect(() => { fetchIdentityStatus(); }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Business-First Frontend Stage 1: `destinationOverride` lets a specific
  // Business capability tile (e.g. POS, which shares the Seller
  // AccountRole with the plain Commerce tile but has its own destination
  // page) land somewhere other than that role's generic home page --
  // still the exact same atomic switchRole() call, same error/nav-reset
  // behavior, never a second switching mechanism.
  const handleSwitchProfile = async (accountRoleId, destinationOverride) => {
    try {
      setRoleSwitchError('');
      const context = await switchRole(accountRoleId);
      setShowProfileSwitcher(false);
      const transition = contextTransitionState(destinationOverride || homeForRole(context.roleType));
      setNavHistory(transition.navHistory);
      setNavParams(transition.navParams);
      setPage(transition.page);
      syncUrl(transition.page, null);
      window.scrollTo(0, 0);
    } catch (error) {
      if (error?.message !== 'ROLE_SWITCH_IN_PROGRESS') {
        setRoleSwitchError(error?.response?.data?.message || error?.message || 'ROLE_SWITCH_FAILED');
      }
    }
  };

  // Analytics — tracks every page, click, scroll automatically
  const { track } = useAnalytics({
    page,
    isLoggedIn,
    userId: (() => {
      try {
        const token = getAccessToken();
        if (!token) return null;
        return JSON.parse(atob(token.split('.')[1]))?.sub || null;
      } catch { return null; }
    })(),
  });

  // One reset boundary for App-owned context state. Component-local state is
  // discarded by the contextEpoch key around the protected page subtree.
  useEffect(() => {
    setNavHistory([]);
    setInboxUnread(0);
    setShowPostModal(false);
    setShowMomentModal(false);
    setShowProfileSwitcher(false);
    setRoleSwitchError('');
    setNavParams(null);
    // Restoring a session from a notification tap must preserve that exact
    // destination; an ordinary role switch still lands on its role home.
    const params = new URLSearchParams(window.location.search);
    const receive = params.get('receive');
    // Same for the seller's new-order link (SMS / WhatsApp): a logged-in seller opening it
    // must land on that order, not on the role home.
    const sellerCheckout = params.get('sellerCheckout');
    if (activeContext?.roleType) setPage(
      receive && /^KTX-[A-Z0-9-]+$/i.test(receive)
        ? `BuyerParcelAction-${receive}`
        : sellerCheckout && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(sellerCheckout)
          ? `SellerCheckout-${sellerCheckout}`
          : params.get('notification') === '1' || /^\d+$/.test(params.get('notificationId') || '')
            ? 'Activity' : homeForRole(activeContext.roleType));
  }, [contextEpoch, activeContext?.roleType]);

  // Syncs the visible browser URL for the 4 shareable content types
  // (product/classified/service/store) — every other page intentionally
  // leaves the URL untouched, matching today's behavior.
  const syncUrl = (pageName, params, state = { page: pageName }, mode = 'push') => {
    const path = pageToPath(pageName, params);
    if (path) window.history[mode === 'replace' ? 'replaceState' : 'pushState'](state, '', path);
  };

  const navigation = useNavigationShell({
    page, setPage, navParams, setNavParams, navHistory, setNavHistory,
    isAuthenticated: isLoggedIn, activeContext, capabilities: roleContext.capabilities,
    contextEpoch, syncUrl,
  });

  // Direct boot/auth/deep-link state changes pass through the same policy as
  // onNavigate. This prevents a stale bookmark or notification from rendering
  // one frame of another role's operational UI.
  useEffect(() => {
    if (authStatus === 'restoring') return;
    const decision = evaluateDestination({
      page, isAuthenticated: isLoggedIn, roleType: activeContext?.roleType,
      capabilities: roleContext.capabilities,
    });
    if (!decision.allowed) navigation.replace(page, null, 'replace');
  }, [page, authStatus, isLoggedIn, activeContext?.roleType, contextEpoch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNavigate = (pageName, params = null) => {
    if (pageName === 'back') {
      return navigation.back();
    }
    // Leaving the inbox is the one moment we know for sure messages might
    // have just been read (their unreadCount reset server-side) — refetch
    // the badge count now instead of waiting for the next full page load.
    const leavingInbox = typeof page === 'string'
      && (page.startsWith('SellerInbox') || page.startsWith('MessageSeller') || page.startsWith('MessageOperational'));
    if (leavingInbox && pageName !== page) fetchInboxUnread();

    return navigation.navigate(pageName, params);
  };

  // Browser back/forward — re-derive the page from the URL the user landed
  // on. Only fires for real navigation events (not our own pushState calls,
  // which don't trigger popstate), so this is purely additive to the
  // existing in-app "back" button logic above.
  useEffect(() => {
    const onPopState = () => {
      const next = pathToPage(window.location.pathname);
      if (next) {
        navigation.handleBrowserDestination(next,
          pathToNavParams(window.location.pathname, window.location.search), window.history.state);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [navigation]);

  const handleLoginSuccess = async (data, options = {}) => {
    try {
      if (data) await acceptAuthResponse(data);
      if (options.refreshOnly) await refreshContext();
      if (getAccessToken()) {
        // Fetch full profile first — avatarUrl can't be read from the JWT
        // (it's signed before the mandatory-photo step completes, so a baked-in
        // claim would go stale). Await it so the photo gate can act on live data.
        let profile = null;
        try {
          const res = await api.get('/auth/profile');
          profile = res.data;
          setCurrentUser(profile);
          // Subscribe to push notifications in background
          if ('Notification' in window && Notification.permission === 'granted') {
            subscribeToPush(process.env.REACT_APP_API_URL || '').catch(() => {});
          }
          localStorage.setItem('kentexa_user', JSON.stringify(profile));
        } catch {}
        setNavParams(null);
        if (profile && !profile.avatarUrl) {
          setPage('AddProfilePhoto');
          return;
        }
        if (profile && profile.onboardingCompleted === false) {
          setPage('Onboarding');
          return;
        }
        {
          // Personal photo setup finishes before consuming the user's destination.
          // Check if there's a stored intended destination (e.g. from + menu)
          const intended = localStorage.getItem('kentexa_after_login');
          if (intended) {
            localStorage.removeItem('kentexa_after_login');
            setPage(intended);
          } else if (!options.targetPage) {
            // Landing Localization L1 (§12-13): campaign intent's one defined
            // consumption point — read and cleared together (consumeIntent)
            // so it can only ever redirect this one post-auth landing, same
            // priority tier as the plain 'Home' fallback it replaces. Never
            // overrides an explicit targetPage a caller already asked for.
            const intentDestination = destinationForIntent(consumeIntent());
            const params = new URLSearchParams(window.location.search);
            const receive = params.get('receive');
            // The seller's new-order link asks for login first; after login it opens that order.
            const sellerCheckout = params.get('sellerCheckout');
            setPage(receive && /^KTX-[A-Z0-9-]+$/i.test(receive)
              ? `BuyerParcelAction-${receive}`
              : sellerCheckout && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(sellerCheckout)
                ? `SellerCheckout-${sellerCheckout}`
                : params.get('notification') === '1' || /^\d+$/.test(params.get('notificationId') || '')
                  ? 'Activity' : (intentDestination || 'Home'));
          } else {
            setPage(options.targetPage);
          }
        }
      }
    } catch (e) {
      roleContext.clearLocalAuthority('login_initialization_failed');
      throw e;
    }
  };

  // Load legacy/presentation profile only after the server has restored the
  // authoritative ActiveRoleSession and AccountRole context.
  useEffect(() => {
    if (isLoggedIn) {
      api.get('/auth/profile').then(res => {
        setCurrentUser(res.data);
        localStorage.setItem('kentexa_user', JSON.stringify(res.data));
        if (!res.data.avatarUrl) setPage('AddProfilePhoto');
        else if (res.data.onboardingCompleted === false) setPage('Onboarding');
      }).catch(() => {});
    }
  }, [isLoggedIn, contextEpoch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch the profile whenever the tab regains focus. currentUser.activeRoles
  // (what every role gate — hasAnyRole — actually checks) is otherwise only
  // ever fetched once per session: a seller approved by an admin while still
  // logged in stays locked out of seller-only pages until they log out and
  // back in, since nothing else refreshes it. Throttled so switching tabs
  // repeatedly doesn't spam the endpoint.
  const lastProfileRefreshRef = React.useRef(0);
  useEffect(() => {
    const refreshProfile = () => {
      if (!isLoggedIn || document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastProfileRefreshRef.current < 30000) return;
      lastProfileRefreshRef.current = now;
      api.get('/auth/profile').then(res => {
        setCurrentUser(res.data);
        localStorage.setItem('kentexa_user', JSON.stringify(res.data));
      }).catch(() => {});
    };
    document.addEventListener('visibilitychange', refreshProfile);
    window.addEventListener('focus', refreshProfile);
    return () => {
      document.removeEventListener('visibilitychange', refreshProfile);
      window.removeEventListener('focus', refreshProfile);
    };
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Boot-time /@username routing — resolves a public commerce-identity
  // handle (e.g. kentexa.com/@bishoo_intelligence_syst) straight to that
  // profile's owner. Can't be read synchronously like ?track=/?confirm=
  // in the page useState initializer above because it needs a network
  // round trip to resolve the username to an owner id; runs once on mount
  // instead, same as the other boot effects here.
  useEffect(() => {
    try {
      const match = window.location.pathname.match(/^\/@([a-z0-9_]+)\/?$/i);
      if (!match) return;
      api.get(`/profiles/username/${match[1]}`).then(res => {
        if (res.data?.ownerId) {
          // Carries the SPECIFIC profile that was resolved (not just the
          // owning account) so the page renders exactly that identity —
          // never a shared "all of this owner's roles" view. See
          // CommerceProfile.js's commerceProfileId prop.
          setNavParams({ commerceProfileId: res.data.id });
          setPage(`CommerceProfile-${res.data.ownerId}`);
        }
      }).catch(() => {});
    } catch { /* no window — SSR */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Boot-time /verify/:code routing (spec §14 serial/IMEI authenticity) —
  // a QR code printed on a physical unit points here. No network round
  // trip needed to resolve the route itself (unlike /@username above) —
  // VerifyProduct.js does its own GET /products/verify/:code — so this
  // just needs to recognize the path and hand off the raw code. A serial
  // can contain letters/digits/hyphens, so this can't reuse urlSync.js's
  // PREFIX_TO_SEGMENT map (which assumes a trailing numeric id).
  useEffect(() => {
    try {
      const match = window.location.pathname.match(/^\/verify\/([A-Za-z0-9-]+)\/?$/);
      if (!match) return;
      setNavParams({ code: match[1] });
      setPage('VerifyProduct');
    } catch { /* no window — SSR */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Lets any page (profile edit, avatar upload, etc.) push a fresh user
  // object up into App's currentUser state after a save — without this,
  // BottomNav/HomeFeed keep reading the currentUser snapshot taken at
  // login (also cached in localStorage, so even a page reload wouldn't
  // pick up the change) and a newly-added photo never appears outside
  // the page that set it.
  const handleUserUpdated = (updatedUser) => {
    setCurrentUser(updatedUser);
    localStorage.setItem('kentexa_user', JSON.stringify(updatedUser));
  };

  const handleLogout = async () => {
    await disablePushNotifications().catch(() => {});
    await roleLogout();
    setCurrentUser(null);
    localStorage.removeItem('kentexa_user');
    setPage('Home');
    setNavParams(null);
  };



  const publicProps = {
    onNavigate: handleNavigate, isLoggedIn, onLogout: handleLogout, userRole, currentUser,
    canInstallKentexa: canInstall, onInstallKentexa: handleInstall,
    onUserUpdated: handleUserUpdated,
    activeProfile, activeProfileId: activeProfile?.id,
    activeContext, availableRoles: roleContext.availableRoles,
    onRefreshContext: roleContext.refreshContext,
    // Business-First Frontend Stage 1: roleOptions already carries
    // businessId/businessName/workspaceId per server-issued role
    // (rolePresentation.js) -- Business Home/My Businesses read it for
    // presentation only, never as an authority source. onSwitchAccountRole
    // is the SAME atomic switch handler the profile switcher already uses
    // (preserves old context on failure, resets nav on success) -- no
    // second switching mechanism.
    roleOptions, onSwitchAccountRole: handleSwitchProfile,
    // Business-First Frontend Stage 1: the current Business's name, for
    // shell/header presentation only ("Bishoo Intelligence Systems /
    // Commerce" instead of just "Seller"). Resolved from roleOptions
    // (which carries businessName) matched against activeContext.accountRoleId
    // -- never persisted as its own authority, never used to derive access.
    activeBusinessName: activeBusinessNameFor(activeContext, roleOptions),
    capabilities: roleContext.capabilities, contextEpoch,
    onOpenMoment: () => {
      if (!isLoggedIn) { handleNavigate('PublicLogin'); return; }
      setMomentModalMode('selling');
      setShowMomentModal(true);
    },
    momentRefreshKey,
    inboxUnread,
  };
  const adminProps  = { onNavigate: handleNavigate, onLogout: handleLogout };

  const loginPage = <PublicLogin onNavigate={handleNavigate} onLoginSuccess={handleLoginSuccess} />;

  const requireLogin = (component) => {
    const token = getAccessToken();
    if (!token) return loginPage;
    return isLoggedIn ? component : loginPage;
  };

  const requireAdmin = (component) => {
    const token = getAccessToken();
    if (!token) return loginPage;
    const role = activeContext?.roleType;
    if (role === 'admin' || role === 'manager') return component;
    return <HomeFeed {...publicProps} />;
  };

  // Identity-verification architecture audit (2026-08-28): closes every
  // entry point into the seller back office (Quick Actions "Seller
  // Center" tile, direct deep link, bottom nav — whatever got the user
  // here) behind identity verification, at the single router level,
  // rather than each page discovering separately that it was never
  // gated. This is about IDENTITY (NIDA/passport/etc.) only — level 1 is
  // enough to pass; whether a given ACTION inside also needs an
  // approved SellerProfile (level 2) is decided separately, backend-
  // side, by VerificationService.requireFeature() per endpoint. Staff
  // (admin/manager) bypass, same as the backend's own shipment gate —
  // they access these pages administratively, not as a commerce
  // participant selling their own goods. Known gap: a BusinessTeamMember
  // (SellerTeam.js — a cashier with e.g. canOperatePOS) is gated on
  // THEIR OWN identity here, not their verified employer's, unlike the
  // backend action-level checks (which correctly resolve to the
  // employer via SellerScopeService). Zero active team members exist in
  // production as of this fix (confirmed via DB), so this is currently
  // inert, not a live regression — revisit if team accounts start
  // getting used and a legitimate unverified staff member gets blocked.
  const requireVerifiedSeller = (component) => {
    const token = getAccessToken();
    if (!token) return loginPage;
    const role = activeContext?.roleType;
    if (role === 'admin' || role === 'manager' || role === 'super_agent') return component;
    if (identityStatus === null) return null; // brief post-login load; avoid flashing either state
    // Seller pages need a VERIFIED ID (server rule). Missing/rejected -> the ID step;
    // waiting -> "kinasubiri" (never asked again). utils/identityState.
    if (identityState(identityStatus) !== 'verified') {
      return (
        <SellerAccessGate
          onNavigate={handleNavigate}
          identityStatus={identityStatus}
          onVerified={() => setIdentityStatus(prev => afterIdentitySent(prev))}
        />
      );
    }
    return component;
  };

  const needsProfilePhoto = isLoggedIn && !!currentUser && !currentUser.avatarUrl;

  const needsOnboarding = isLoggedIn && currentUser?.onboardingCompleted === false;

  const renderPage = () => {
    if (needsProfilePhoto) return <AddProfilePhoto {...publicProps} />;
    if (needsOnboarding) return <Onboarding {...publicProps} />;
    const pageStr = typeof page === 'string' ? page : 'Home';
    const policyDecision = evaluateDestination({
      page: pageStr, isAuthenticated: isLoggedIn, roleType: activeContext?.roleType,
      capabilities: roleContext.capabilities,
    });
    // The effect above replaces invalid state with the current role home. Render
    // nothing during that transition so wrong-context operational UI never flashes.
    if (pageStr !== page || !policyDecision.allowed) return null;

    // ── Dynamic routes (single source of truth — do not duplicate below) ────
    // "New comment"/"New save" notifications append "-comments" so the
    // detail page opens straight on its comment thread instead of its
    // default view — split it off before Number() parsing so a suffix can
    // never turn the id into NaN (see CommerceProfile's pageParam parsing
    // for the same fix, applied earlier this session).
    if (page.startsWith('ClassifiedDetail-')) {
      const [rawId, suffix] = page.split('ClassifiedDetail-')[1].split('-');
      return <ClassifiedDetail {...publicProps} classifiedId={Number(rawId)} openComments={suffix === 'comments'} />;
    }
    if (page.startsWith('ProductDetail-')) {
      const [rawId, suffix] = page.split('ProductDetail-')[1].split('-');
      return <ProductDetail {...publicProps} productId={Number(rawId)} track={track}
        initialTab={suffix === 'comments' ? 'reviews' : null} />;
    }
    // Deep-links from a warranty-claim notification (WarrantyService's own
    // actionPage/actionParam) straight to the relevant registration —
    // see MyWarranties.js/SellerWarrantyClaims.js's own registrationId prop.
    if (page.startsWith('MyWarranties-'))
      return requireLogin(<MyWarranties {...publicProps} registrationId={page.split('MyWarranties-')[1]} />);
    if (page.startsWith('SellerWarrantyClaims-'))
      return requireLogin(<SellerWarrantyClaims {...publicProps} registrationId={page.split('SellerWarrantyClaims-')[1]} />);
    // Deep-links straight into editing one item — from that item's own
    // detail page ••• menu, so the owner doesn't have to re-find it on the
    // separate SellerProducts/SellerClassifieds management page.
    if (page.startsWith('EditProduct-'))
      return requireLogin(<SellerProducts {...publicProps} editProductId={Number(page.split('EditProduct-')[1])} />);
    if (page.startsWith('EditClassified-'))
      return requireLogin(<SellerClassifieds {...publicProps} editItemId={Number(page.split('EditClassified-')[1])} />);
    if (page.startsWith('ServiceDetail-')) {
      const [rawId, suffix] = page.split('ServiceDetail-')[1].split('-');
      return <ServiceDetail {...publicProps} serviceId={Number(rawId)} openComments={suffix === 'comments'} />;
    }
    if (page.startsWith('BusinessDashboard-'))
      return requireLogin(<BusinessDashboard {...publicProps} businessId={Number(page.split('BusinessDashboard-')[1])} />);
    if (page.startsWith('BusinessHome-'))
      return requireLogin(<BusinessHome {...publicProps} businessId={Number(page.split('BusinessHome-')[1])} />);
    if (page.startsWith('BecomeBusinessCapability-')) {
      const [, id, code] = page.split('-');
      if (['commerce', 'transport'].includes(code)) return requireLogin(<BecomeBusinessCapability {...publicProps} businessId={Number(id)} code={code} />);
    }
    if (page.startsWith('BecomeBusinessServiceProvider-'))
      return requireLogin(<BecomeBusinessServiceProvider {...publicProps} businessId={Number(page.split('BecomeBusinessServiceProvider-')[1])} />);
    if (page.startsWith('SellerStore-'))
      return <SellerStore {...publicProps} sellerId={page.split('SellerStore-')[1]} />;
    // Store- is a legacy alias — route it through the same CommerceProfile
    // page as everywhere else, so there is only ever ONE seller-profile
    // implementation (and therefore only one source of truth for follow state).
    if (page.startsWith('Store-'))
      return <CommerceProfile {...publicProps} pageParam={page.split('Store-')[1]} track={track} />;
    if (page.startsWith('CommerceProfile-')) {
      const pid = page.split('CommerceProfile-')[1];
      return <CommerceProfile {...publicProps} pageParam={pid} commerceProfileId={navParams?.commerceProfileId} track={track} />;
    }
    // Order-lifecycle notifications (orderPlaced, orderPaid, disputeRaised,
    // payoutReleased, ...) carry actionPage + orderId as actionParam. Without
    // these, Activity.js builds "MyOrders-42" and the switch below has no
    // case for that literal string, so it silently fell through to Home
    // instead of even the plain list.
    if (page.startsWith('MyOrders-'))
      return requireLogin(<MyOrders {...publicProps} highlightOrderId={Number(page.split('MyOrders-')[1])} />);
    if (page.startsWith('SellerOrders-'))
      return requireLogin(<SellerOrders {...publicProps} highlightOrderId={Number(page.split('SellerOrders-')[1])} />);
    if (page.startsWith('SellerPayouts-'))
      return requireLogin(<SellerPayouts {...publicProps} highlightOrderId={Number(page.split('SellerPayouts-')[1])} />);
    // Tracking must work without an account — the recipient of a parcel
    // often isn't the buyer and may not be logged in at all.
    if (page.startsWith('TrackParcel-'))
      return <TrackParcel {...publicProps} trackingNumber={page.split('TrackParcel-')[1]} />;
    if (page.startsWith('TrackOrder-'))
      return <TrackParcel {...publicProps} orderId={page.split('TrackOrder-')[1]} />;
    if (page.startsWith('OrderTracking-'))
      return requireLogin(<OrderTracking {...publicProps} orderId={Number(page.split('OrderTracking-')[1])} />);
    if (page.startsWith('ConfirmDelivery-'))
      return <ConfirmDelivery token={page.split('ConfirmDelivery-')[1]} />;
    if (page.startsWith('SellerCheckout-'))
      return requireLogin(<SellerCheckout token={page.slice('SellerCheckout-'.length)} onNavigate={publicProps.onNavigate} />);
    if (page.startsWith('BuyerParcelAction-'))
      return requireLogin(<BuyerParcelAction {...publicProps} trackingNumber={page.split('BuyerParcelAction-')[1]} />);
    if (page.startsWith('BatchHandoff-'))
      return requireLogin(<BatchHandoff {...publicProps} orderId={page.split('BatchHandoff-')[1]} />);
    if (page.startsWith('PayInvoice-'))
      return <PayInvoice {...publicProps} prefilledOrderId={page.split('PayInvoice-')[1]} />;
    if (page.startsWith('VerifyReceipt-'))
      return <VerifyReceipt {...publicProps} initialReceipt={page.split('VerifyReceipt-')[1]} />;
    if (page.startsWith('SellerInbox-')) {
      const customerId = page.split('SellerInbox-')[1];
      return requireLogin(<SellerInbox {...publicProps} initialCustomerId={customerId} />);
    }
    if (page.startsWith('MessageSeller-')) {
      const sellerId = page.split('MessageSeller-')[1];
      return requireLogin(<SellerInbox {...publicProps} initialCustomerId={null} sellerId={sellerId}
        messageCommerceProfileId={navParams?.commerceProfileId}
        messageContextType={navParams?.contextType} messageContextId={navParams?.contextId} />);
    }
    // Communication canonicality fix: messaging a Super Agent/Transport
    // Provider/Agent profile must NOT collapse onto that same person's
    // Seller conversation just because MessageSeller-{id} only ever
    // carried the bare User.id. `commerceProfileId` here is the SAME id
    // CommerceProfile.js already has as activeProfile.id -- resolved
    // server-side (see POST /business/my-conversations/start), never
    // trusted as authority by itself.
    if (page.startsWith('MessageOperational-')) {
      const [, targetType, commerceProfileId] = page.split('-');
      return requireLogin(<SellerInbox {...publicProps} initialCustomerId={null}
        messageTargetType={targetType} messageTargetId={commerceProfileId}
        messageContextType={navParams?.contextType} messageContextId={navParams?.contextId} />);
    }
    if (page.startsWith('Search-'))
      return <Search {...publicProps} initialQuery={page.split('Search-')[1]} aiIntent={navParams?.aiIntent} track={track} />;
    if (page.startsWith('Category-'))
      return <CategoryPage {...publicProps} category={page.split('Category-')[1]} />;
    if (page.startsWith('Listings-'))
      return <ListingsPage {...publicProps} initialTab={page.split('Listings-')[1]} />;
    if (page === 'Listings')
      return <ListingsPage {...publicProps} />;
    switch (page) {
      case 'Home':              return isLoggedIn ? <HomeFeed {...publicProps} /> : <Welcome {...publicProps} />;

      case 'AboutUs':              return <AboutUs {...publicProps} />;
      case 'ContactUs':            return <ContactUs {...publicProps} />;
      case 'HowItWorks':           return <HowItWorks {...publicProps} />;
      case 'Privacy':
      case 'PrivacyPolicy':              return <PrivacyPolicy {...publicProps} />;
      case 'Terms':
      case 'TermsAndConditions':   return <TermsAndConditions {...publicProps} />;
      case 'FlashSales':           return <FlashSales {...publicProps} />;
      case 'PickupPoints':         return <PickupPoints {...publicProps} />;
      case 'ClassifiedDetail':     return <ClassifiedDetail {...publicProps} />;
      case 'ServiceDetail':        return <ServiceDetail {...publicProps} />;
      case 'ProductDetail':        return <ProductDetail {...publicProps} />;
      case 'VerifyProduct':        return <VerifyProduct {...publicProps} code={navParams?.code} />;
      case 'MyWarranties':         return requireLogin(<MyWarranties {...publicProps} />);
      case 'SellerWarrantyClaims': return requireLogin(<SellerWarrantyClaims {...publicProps} />);
      case 'OrderTracking':        return requireLogin(<OrderTracking {...publicProps} />);

      case 'Discover':          return <Search {...publicProps} track={track} />;
      case 'Activity':          return requireLogin(<Activity {...publicProps} />);
      case 'RoleActivation':    return requireLogin(<RoleActivation {...publicProps} />);
      case 'Onboarding':        return requireLogin(<Onboarding {...publicProps} />);
      case 'AddProfilePhoto':   return requireLogin(<AddProfilePhoto {...publicProps} />);
      case 'RouteCoverageMap':   return <RouteCoverageMap {...publicProps} />;
      case 'MyProfile':         return requireLogin(<MyProfile {...publicProps} />);
      case 'CommerceProfile':     return requireLogin(<CommerceProfile {...publicProps} pageParam={null} track={track} />);
      case 'Store':
      case 'Stores':            return <Stores {...publicProps} />;
      case 'ClassifiedsPublic': return <ClassifiedsPublic {...publicProps} />;
      case 'PublicLogin':       return loginPage;
      case 'Register':          return <Register onNavigate={handleNavigate} onLoginSuccess={handleLoginSuccess} />;
      case 'Search':            return <Search {...publicProps} aiIntent={navParams?.aiIntent} track={track} />;
      case 'Cart':              return <Cart {...publicProps} />;
      case 'BecomeSeller':      return <BecomeSeller {...publicProps} />;
      case 'BecomeSellerInfo':  return <BecomeSellerInfo {...publicProps} />;
      case 'SellerCustomers':   return requireVerifiedSeller(<SellerCustomers {...publicProps} />);
      case 'AgentEarnings':     return requireLogin(<AgentEarnings {...publicProps} />);
      case 'SellerPayouts':     return requireVerifiedSeller(<SellerPayouts {...publicProps} />);
      case 'SellerWallet':      return requireVerifiedSeller(<SellerWallet {...publicProps} />);
      case 'BecomeAgent':       return <BecomeAgent {...publicProps} />;
      case 'BecomeSuperAgentInfo': return <BecomeSuperAgentInfo {...publicProps} />;
      case 'VerifyReceipt':     return <VerifyReceipt {...publicProps} />;
      case 'PayInvoice':        return <PayInvoice {...publicProps} />;

      case 'MyOrders':          return requireLogin(<MyOrders {...publicProps} />);
      case 'EditPublicProfile': return requireLogin(<EditPublicProfile {...publicProps} commerceProfileId={navParams?.commerceProfileId} />);
      case 'PayoutSettings': return requireLogin(<PayoutSettings {...publicProps} />);
      case 'CustomerProfile':   return requireLogin(<CustomerProfile {...publicProps} editField={navParams?.editField} />);
      case 'Checkout':          return requireLogin(<Checkout {...publicProps} />);
      case 'StoreSettings':     return requireVerifiedSeller(<StoreSettings {...publicProps} userId={activeContext?.userId} />);
      case 'SellerDashboard':   return requireVerifiedSeller(<SellerDashboard {...publicProps} />);
      case 'BusinessDashboard': return requireLogin(<BusinessDashboard {...publicProps} />);
      case 'MyBusinesses':      return requireLogin(<MyBusinesses {...publicProps} />);
      case 'ConnectSelling':    return requireLogin(<ConnectSelling {...publicProps} />);
      case 'BusinessHome':      return requireLogin(<BusinessHome {...publicProps} businessId={null} />);
      case 'MyClassifieds':     return requireLogin(<SellerClassifieds {...publicProps} listingMode="personal" />);
      case 'CreateClassified':  return requireLogin(<SellerClassifieds {...publicProps} listingMode="personal" createOnly />);
      case 'BecomeBusiness':    return <BecomeBusiness {...publicProps} />;
      case 'SellerProducts':    return requireLogin(<SellerProducts {...publicProps} />);
      case 'MyBrands':          return requireVerifiedSeller(<MyBrands {...publicProps} />);
      case 'BrandDashboard':    return requireLogin(<BrandDashboard {...publicProps} />);
      case 'BrandCatalog':      return requireLogin(<BrandCatalog {...publicProps} />);
      case 'POS':               return requireVerifiedSeller(<POS {...publicProps} />);
      case 'SellerClassifieds': return requireLogin(<SellerClassifieds {...publicProps} listingMode="business" />);
      case 'SellerOrders':      return requireVerifiedSeller(<SellerOrders {...publicProps} />);
      case 'SellerShipping':    return requireVerifiedSeller(<SellerShipping {...publicProps} />);
      case 'SendShipment':      return requireLogin(<SendShipment {...publicProps} navParams={navParams} />);
      case 'MyShipments':       return requireLogin(<MyShipments {...publicProps} />);
      case 'SellerInvoices':    return requireVerifiedSeller(<SellerInvoices {...publicProps} preSelected={navParams?.preSelected} />);
      case 'AgentDashboard':    return requireLogin(<AgentDashboard {...publicProps} />);
      case 'AgentOrderDashboard': return requireLogin(<AgentDashboard {...publicProps} />); // merged into unified dashboard
      case 'SuperAgentDashboard':   return requireLogin(<SuperAgentDashboard {...publicProps} />);
      case 'DispatcherManifest':    return requireLogin(<DispatcherManifest {...publicProps} />);
      case 'HubReceive':           return requireLogin(<HubReceive {...publicProps} />);
      case 'BatchHandoff':          return requireLogin(<BatchHandoff {...publicProps} />);
      case 'SuperAgentParcel':       return requireLogin(<SuperAgentParcel {...publicProps} />);
      case 'SuperAgentSettings':     return requireLogin(<SuperAgentSettings {...publicProps} />);
      case 'SellerShipment':         return requireVerifiedSeller(<SellerShipment {...publicProps} prefill={navParams} />);
      case 'Dashboard':   return requireAdmin(<Dashboard activePage={page} {...adminProps} />);
      case 'Products':    return requireAdmin(<Products activePage={page} {...adminProps} />);
      case 'Classifieds': return requireAdmin(<Classifieds activePage={page} {...adminProps} />);
      case 'Users':       return requireAdmin(<Users activePage={page} {...adminProps} />);
      case 'Orders':      return requireAdmin(<Orders activePage={page} {...adminProps} />);
      case 'AdminShipments': return requireAdmin(<AdminShipments activePage={page} {...adminProps} />);
      case 'AdminDispatchReminders': return requireAdmin(<AdminDispatchReminders activePage={page} {...adminProps} />);
      case 'Payments':    return requireAdmin(<Payments activePage={page} {...adminProps} />);
      case 'Sellers':     return requireAdmin(<Sellers activePage={page} {...adminProps} />);
      case 'Businesses':  return activeContext?.roleType === 'admin'
        ? requireAdmin(<Businesses activePage={page} {...adminProps} />)
        : <HomeFeed {...publicProps} />;
      // 'AdminServices', not 'Services' — that page key is already taken by
      // the public services-browse page below; a duplicate case label would
      // have silently made this the only one that ever executes, breaking
      // the public page for every user.
      case 'AdminServices': return requireAdmin(<AdminServices activePage={page} {...adminProps} />);
      case 'IdentityVerifications': return requireAdmin(<IdentityVerifications activePage={page} {...adminProps} />);
      case 'AdminBrands': return requireAdmin(<AdminBrands activePage={page} {...adminProps} />);
      case 'AdminBrandAuthorizations': return requireAdmin(<AdminBrandAuthorizations activePage={page} {...adminProps} />);
      case 'AdminWarrantyClaims': return requireAdmin(<AdminWarrantyClaims activePage={page} {...adminProps} />);
      case 'OfficialProducts': return requireAdmin(<OfficialProducts activePage={page} {...adminProps} />);
      case 'Agents':      return requireAdmin(<Agents activePage={page} {...adminProps} />);
      case 'Profile':     return requireLogin(<Profile activePage={page} {...adminProps} />);
      case 'TrackParcel': return <TrackParcel {...publicProps} />;
      case 'SuperAgents': return requireAdmin(<SuperAgents activePage={page} {...adminProps} />);
      case 'Disputes':    return requireAdmin(<Disputes    activePage={page} {...adminProps} />);
      case 'Payouts':     return requireAdmin(<Payouts     activePage={page} {...adminProps} />);
      case 'Invoices':    return requireAdmin(<Invoices    activePage={page} {...adminProps} />);
      case 'Reports':        return requireAdmin(<Reports     activePage={page} {...adminProps} />);
      case 'ContactMessages': return requireAdmin(<ContactMessages activePage={page} {...adminProps} />);
      case 'Announcements':    return requireAdmin(<Announcements activePage={page} {...adminProps} />);
      case 'Analytics':        return requireAdmin(<Analytics activePage={page} {...adminProps} />);
      case 'ZoneManagement':    return requireAdmin(<ZoneManagement onNavigate={handleNavigate} onLogout={handleLogout} />);
      case 'BodaRates':         return requireAdmin(<BodaRates onNavigate={handleNavigate} onLogout={handleLogout} />);
      case 'RouteManagement':   return requireAdmin(<RouteManagement onNavigate={handleNavigate} onLogout={handleLogout} />);
      case 'CollectionFees':    return requireAdmin(<CollectionFees onNavigate={handleNavigate} onLogout={handleLogout} />);
      case 'AgentPerformance':  return requireAdmin(<AgentPerformance onNavigate={handleNavigate} onLogout={handleLogout} />);
      case 'FinancialDashboard': return requireAdmin(<FinancialDashboard onNavigate={handleNavigate} onLogout={handleLogout} />);

      case 'BecomeTransportProvider':    return requireLogin(<BecomeTransportProvider {...publicProps} />);
      case 'TransportProviderDashboard': return requireLogin(<TransportProviderDashboard {...publicProps} />);
      case 'TransportProviderSettings': return requireLogin(<TransportProviderSettings {...publicProps} />);
      case 'TransportAdmin':             return requireAdmin(<TransportAdmin onNavigate={handleNavigate} activePage={page} />);
      case 'VanOperations':              return requireAdmin(<VanOperations onNavigate={handleNavigate} activePage={page} />);
      case 'HubAdmin':                   return requireAdmin(<HubAdmin onNavigate={handleNavigate} activePage={page} />);
      case 'SellerAnalytics':            return requireVerifiedSeller(<SellerAnalytics {...publicProps} />);
      case 'SellerTeam':                 return requireVerifiedSeller(<SellerTeam {...publicProps} />);
      case 'AgentScorecard':             return requireLogin(<AgentScorecard {...publicProps} />);
      case 'Wishlist':                   return requireLogin(<Wishlist {...publicProps} />);
      case 'SellerInbox':                return requireLogin(<SellerInbox {...publicProps} />);

      case 'Services':                     return <Services {...publicProps} />;
      case 'PostService':                  return requireLogin(<PostService {...publicProps} />);
      case 'MyServices':                   return requireVerifiedSeller(<MyServices {...publicProps} />);
      default: return <HomeFeed {...publicProps} />;
    }
  };

  const handleLangPick = (lang) => {
    changeAppLanguage(i18n, lang);
    setShowLangPicker(false);
  };

  if (authStatus === 'restoring') {
    return <div role="status" style={{ minHeight:'100vh', display:'grid', placeItems:'center', color:'#64748b' }}>Loading…</div>;
  }

  return (
    <OnboardingProvider currentUser={currentUser}>
    <CartProvider>
      <ContextEpochBoundary contextEpoch={contextEpoch}>{renderPage()}</ContextEpochBoundary>

      {/* A phone that kept the app open gets a new website version by itself, never mid-form. */}
      <UpdateNotice />
      <TrainingSmsNotice />

      {/* ── Social Commerce Navigation ─────────────────────── */}
      {/* Hidden during Onboarding — a focused setup wizard shouldn't show
          tab navigation, and this also fixes the Continue button being
          hidden behind the nav bar on each step. Also hidden on the Inbox
          (SellerInbox / SellerInbox-{id} / MessageSeller-{id}, same
          startsWith check already used above for the unread-refetch-on-exit
          logic): SellerInbox.js is its own full 100dvh chat surface with a
          message composer pinned to the true bottom edge — this fixed,
          position:fixed, zIndex:1000 bar was rendering directly on top of
          it, covering the send button (and on the conversation list, the
          bottom rows) since nothing in SellerInbox reserved space for it. */}
      {isLoggedIn && !needsProfilePhoto && !needsOnboarding && page !== 'Onboarding' && page !== 'AddProfilePhoto' && page !== 'POS'
        && !(typeof page === 'string' && (page.startsWith('SellerInbox') || page.startsWith('MessageSeller') || page.startsWith('MessageOperational'))) && (
        <BottomNav
          // Remount protection (profile-switch architecture spec): a stable
          // profile-context key forces React to tear down and rebuild this
          // subtree on every profile switch rather than patch it in place.
          // BottomNav itself holds no internal state that could go stale —
          // its tabs are already fully re-derived from activeProfile on
          // every render — but this makes "one active profile = one fresh
          // navigation instance" true structurally, not just by convention,
          // so a future change that DOES add local nav state can't
          // reintroduce the leak-across-switches bug this key guards against.
          key={activeContext ? `role-${activeContext.accountRoleId}-${contextEpoch}` : 'no-context'}
          currentPage={page}
          onNavigate={handleNavigate}
          isLoggedIn={isLoggedIn}
          userRole={userRole}
          activeContext={activeContext}
          currentUser={currentUser}
          onPostClick={() => setShowPostModal(true)}
          activeProfile={activeProfile}
          activeBusinessName={activeBusinessNameFor(activeContext, roleOptions)}
          myProfiles={myProfiles}
          onOpenSwitcher={() => setShowProfileSwitcher(true)}
          switching={roleSwitching}
        />
      )}
      {showProfileSwitcher && (
        <ProfileSwitcherSheet
          profiles={myProfiles}
          activeAccountRoleId={activeContext?.accountRoleId}
          onSwitch={handleSwitchProfile}
          onClose={() => setShowProfileSwitcher(false)}
          onNavigate={handleNavigate}
          onOpenBusiness={(businessId) => { setShowProfileSwitcher(false); handleNavigate(`BusinessHome-${businessId}`); }}
          switching={roleSwitching}
          error={roleSwitchError}
        />
      )}
      {showPostModal && (
        <PostModal
          onNavigate={handleNavigate}
          onClose={() => setShowPostModal(false)}
          activeProfile={activeProfile}
          onOpenMoment={(mode) => {
            setShowPostModal(false);
            setMomentModalMode(mode);
            setShowMomentModal(true);
          }}
        />
      )}
      {showMomentModal && (
        <CreateMomentModal
          currentUser={currentUser}
          initialMode={momentModalMode}
          activeProfileId={momentActorProfileId(activeProfile) ?? null}
          activeProfile={activeProfile}
          onClose={() => setShowMomentModal(false)}
          onPosted={() => {
            setMomentRefreshKey(k => k + 1);
            if (page !== 'Home') handleNavigate('Home');
          }}
        />
      )}

      {/* First-visit language picker — Landing Localization L1 (§2): buttons
          now come from the canonical SUPPORTED_LANGUAGES list instead of 3
          hardcoded ones, and the modal's own copy is translated via t()
          rather than hand-written bilingual text. i18n.js has already
          resolved a sensible initial language (URL/saved/browser/default)
          by the time this renders, so t() here always has something sane
          to render even before the visitor picks anything. */}
      {showLangPicker && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 20, padding: '32px 24px', width: '100%', maxWidth: 340, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🇹🇿</div>
            <h2 style={{ fontSize: 20, fontWeight: 900, color: '#1e293b', margin: '0 0 6px', fontFamily: 'Manrope,sans-serif' }}>{t('welcome.language_picker.title')}</h2>
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 24px' }}>{t('welcome.language_picker.subtitle')}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button key={lang.code} onClick={() => handleLangPick(lang.code)}
                  style={{ padding: '14px', borderRadius: 12, border: lang.code === 'sw' ? '2px solid #1d4ed8' : '2px solid #e2e8f0', backgroundColor: lang.code === 'sw' ? '#eff6ff' : '#f8fafc', cursor: 'pointer', fontSize: 15, fontWeight: 800, color: lang.code === 'sw' ? '#1d4ed8' : '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                  {lang.flag} {lang.label}
                  {lang.code === 'sw' && <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>({t('welcome.language_picker.recommended')})</span>}
                </button>
              ))}
            </div>
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 16 }}>{t('welcome.language_picker.hint')}</p>
          </div>
        </div>
      )}
      {canInstall && showInstallPrompt && page === 'Home' && !showLangPicker && !showPostModal &&
        !showMomentModal && !showGuide &&
        <InstallBanner onInstall={handleInstall} onDismiss={handleDismiss} />}
      {showGuide && <InstallGuide environment={environment} onClose={closeGuide} />}
    </CartProvider>
    </OnboardingProvider>
  );
}

export default App;
